import { Injectable } from '@angular/core';
import { Media } from '@core/models/atomics.model';
import { Product, PRODUCT_MEDIA_TYPE, ProductImageContainer } from '@b2b/features/product/core/utilities/product.model';

type GenericImageSource = {
  medias?: Product['medias'];
  mediaUrls?: string | string[];
  imageUrls?: string | string[];
};

@Injectable({ providedIn: 'root' })
export class ProductImageService {
  private imageNotFound: ProductImageContainer = {
    superZoom: {
      src: 'assets/medias/images/products/no-picture-1200x1200.png',
    },
    zoom: {
      src: 'assets/medias/images/products/no-picture-515x515.png',
    },
    product: {
      src: 'assets/medias/images/products/no-picture-300x300.png',
    },
    thumbnail: {
      src: 'assets/medias/images/products/no-picture-96x96.png',
    },
    cartIcon: {
      src: 'assets/medias/images/products/no-picture-65x65.png',
    },
    icon: {
      src: 'assets/medias/images/products/no-picture-30x30.png',
    },
  };

  constructor() { }

  getImage(product: Product | undefined, format: PRODUCT_MEDIA_TYPE) {
    return this.getImageContainer(product, format)?.[format];
  }

  getImageContainer(product: GenericImageSource | undefined, format: PRODUCT_MEDIA_TYPE): ProductImageContainer {
    let imageContainer = this.imageNotFound;

    const productImage = product?.medias?.images?.find(container => container[format].src);
    if (productImage) {
      imageContainer = productImage;
    } else {
      const fallbackUrl = this.getFirstAvailableUrl(product);
      if (fallbackUrl) {
        imageContainer = this.createImageContainer(fallbackUrl);
      }
    }

    return imageContainer;
  }

  getImageNotFound(format: PRODUCT_MEDIA_TYPE): Media {
    return this.imageNotFound[format];
  }

  getFallbackImageUrl(format: PRODUCT_MEDIA_TYPE = PRODUCT_MEDIA_TYPE.PRODUCT): string {
    return this.imageNotFound[format].src;
  }

  getPrimaryImageUrl(product: GenericImageSource | undefined, format: PRODUCT_MEDIA_TYPE = PRODUCT_MEDIA_TYPE.PRODUCT): string {
    const image = this.getImageContainer(product, format)?.[format];
    return this.normalizeUrl(image?.src) || this.imageNotFound[format].src;
  }

  getMediaObject(
    product: GenericImageSource | undefined,
    format: PRODUCT_MEDIA_TYPE = PRODUCT_MEDIA_TYPE.PRODUCT,
    altText = '',
  ): Media {
    const image = this.getImageContainer(product, format)?.[format] ?? this.imageNotFound[format];
    return {
      src: this.normalizeUrl(image.src),
      altText: image.altText ?? altText,
    };
  }

  private createImageContainer(url: string): ProductImageContainer {
    const normalizedUrl = this.normalizeUrl(url);

    return {
      superZoom: {
        src: normalizedUrl,
      },
      zoom: {
        src: normalizedUrl,
      },
      product: {
        src: normalizedUrl,
      },
      thumbnail: {
        src: normalizedUrl,
      },
      cartIcon: {
        src: normalizedUrl,
      },
      icon: {
        src: normalizedUrl,
      },
    };
  }

  private getFirstAvailableUrl(product: GenericImageSource | undefined): string | null {
    const mediaUrls = this.toUrlArray(product?.mediaUrls);
    if (mediaUrls.length > 0) {
      return mediaUrls[0];
    }

    const imageUrls = this.toUrlArray(product?.imageUrls);
    return imageUrls.length > 0 ? imageUrls[0] : null;
  }

  private toUrlArray(value: string | string[] | undefined): string[] {
    if (!value) {
      return [];
    }

    return Array.isArray(value) ? value.filter(Boolean) : [value];
  }

  private normalizeUrl(value: string | undefined | null): string {
    const url = String(value ?? '').trim();
    if (!url) {
      return '';
    }

    return url.replace(/^\/src\/\.\.\/assets\//, 'assets/');
  }
}
